// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { lazy } from "react";
import { reloadApp } from "./reloadApp";

const STALE_CHUNK_RELOAD_KEY = "riddle.app.staleChunkReload";
const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;
const STALLED_RELOAD_MS = 10_000;

export class StaleChunkLoadError extends Error {
  readonly title = "This page couldn't load";

  constructor(cause: unknown) {
    super(
      window.navigator.onLine
        ? "Check your connection, then reload."
        : "You're offline. Reconnect, then reload.",
      { cause },
    );
  }
}

const CHUNK_LOAD_FAILURE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/;

function isChunkLoadFailure(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_FAILURE.test(error.message);
}

function claimStaleChunkReload(): boolean {
  try {
    const now = Date.now();
    const lastReload = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
    if (now - lastReload < STALE_CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

export function loadWithStaleChunkReload<T>(load: () => Promise<T>): Promise<T> {
  return load().catch((error: unknown) => {
    if (!isChunkLoadFailure(error)) {
      throw error;
    }
    if (!window.navigator.onLine || !claimStaleChunkReload()) {
      throw new StaleChunkLoadError(error);
    }
    reloadApp();
    return new Promise<T>((_resolve, reject) => {
      window.setTimeout(() => reject(new StaleChunkLoadError(error)), STALLED_RELOAD_MS);
    });
  });
}

export const lazyWithStaleChunkReload: typeof lazy = (load) =>
  lazy(() => loadWithStaleChunkReload(load));
